import React, { useContext, useState } from 'preact/compat';
import AnimateHeight from 'react-animate-height';

import PathChangeContext from 'Flyout/Contexts';
import translator from 'Flyout/Api/Translator';
import NodeChildren from './NodeChildren';

export default function Accordion(props) {
    const { object, treeState, children } = props;
    const [childrenHeight, setChildrenHeight] = useState(0);

    /**
     * Check if a given object is the ancestor of a given target object or the targetObj itself
     * @param {TreeObject} ancestorObject
     * @param {TreeObject} targetObj
     * @returns {Boolean}
     */
    const isAncestorOrSelf = (ancestorObject, targetObj) => targetObj === ancestorObject
        || ancestorObject.isAncestorOf(targetObj);

    // Remember if this unit should currently be open or will be one of the next opened units
    const currentlyOpen = isAncestorOrSelf(object, treeState.currentObject);
    const nextOpen = isAncestorOrSelf(object, treeState.nextObject);
    const showChildren = currentlyOpen || nextOpen;

    // We only want the children to be opened if this unit is not the root of a closed tree and
    // if it is an ancestor of the next opened unit.
    // The currently opened units instead should get closed if they are not an ancestor of the next opened unit.
    if (!object.parent && !treeState.treeOpen) {
        setChildrenHeight(0);
    } else if (nextOpen) {
        setChildrenHeight('auto');
    } else {
        setChildrenHeight(0);
    }

    // Get the methods of the tree's root to change the global tree path.
    const pathChanger = useContext(PathChangeContext);

    /**
     * Handle clicks on the toggle button
     * Hands over the next TreeObject that should get opened to the tree's root
     */
    const handleToggleClick = () => {
        if (object.parent) {
            // normal handling from second level onwards: setNext
            const nextObject = showChildren ? object.parent : object;
            pathChanger.setNext(nextObject);
        } else {
            // on first level (no parent) we need to toggle the root accordion
            pathChanger.toggleNavigation(!treeState.treeOpen);
        }
    };

    /**
     * Handle the animationEnd event
     * Informs the tree's root that animation ended and the nextObject can get the currentObject
     */
    const handleHeightAnimationEnd = () => {
        pathChanger.setCurrent();
    };

    // the classes for the title-container
    const titleClasses = ['title'];

    // Assemble the title (it is a link in case the unit has an url)
    let { title } = object;
    // use link when object has url, is not private marked and is not the root with a root url
    if (typeof object.url !== 'undefined' && object.url && !object.is_private
        && (!!object.parent || object.url !== window.PORTAL_URL)) {
        title = <a href={object.url}>{object.title}</a>;
        titleClasses.push('linked');
    } else { // wrap with span to properly apply styling
        title = <span>{object.title}</span>;
    }

    // The accordion is expanded if
    // - this is the root object of the tree and the tree is currently opened
    // - this is object is part of the open / nextOpen path and it is not the root object
    const expanded = (treeState.treeOpen && !object.parent) || (nextOpen && !!object.parent);

    // Build the jsx of the toggle button
    let accordionToggle = null;
    if (object.hasChildren) {
        accordionToggle = (
            <button
                aria-controls={object.uniqueID}
                aria-expanded={expanded}
                type="button"
                onClick={handleToggleClick}
                className="button-link btn-link toggler-button"
                aria-label={`${translator.i18n(expanded ? 'closeNode' : 'openNode')} ${object.title}`}
            >
                <span className="chevron">
                    <span className="icon-bar" />
                    <span className="icon-bar" />
                </span>
            </button>
        );
        titleClasses.push('has-children');
    }

    // Build the jsx of the children
    const accordion = !object.hasChildren ? null : (
        <AnimateHeight
            id={object.uniqueID}
            className="accordion"
            height={childrenHeight}
            duration={333} // animation time must be in sync with the animation time in our styles (_flyout.scss)
            applyInlineTransitions={false}
            onHeightAnimationEnd={handleHeightAnimationEnd}
        >
            { children }
            { showChildren && <NodeChildren object={object} treeState={treeState} /> }
        </AnimateHeight>
    );

    return (
        <>
            <div className={titleClasses.join(' ')} data-review-state={object.review_state}>
                { title }
                { accordionToggle }
            </div>
            { accordion }
        </>
    );
}
